import { kindShortLabel, kindIsHoldOnly } from '../../lib/phaseKinds';
import React, { useState, useMemo } from 'react';
import { format, addDays, startOfWeek, parseISO, isToday } from 'date-fns';
import { useStore } from '../../store/StoreContext';
import { HelpTip, Button, IconButton } from '../ui';
import JobModal from '../modals/JobModal';
import { formatTime12 } from '../../utils/formatters';
import { useToast } from '../../hooks/useToast';

// ===== DISPATCH — TRUCK BOARD (2026-08-03) =====
// The week through the FLEET's eyes: trucks as rows, days as columns,
// a pill per job a truck is on. A truck landing on two different jobs
// the same day gets the red conflict treatment (same signal as the
// board pill's ⚠, but visible as a whole lane). Read-only — clicking a
// pill opens the shared JobModal; truck REASSIGNMENT stays in the
// AssignModal where the chaining rules live.

// Phase labels generated from segments (phaseKinds.js, 2026-08-26).
// Truck-needing job types — used for the "needs a truck" rail. Mirrors
// the truckJob test in DailyDispatchModal's missing-resources check.
const TRUCK_TYPES = new Set(['LOCAL', 'LONG_DISTANCE', 'DELIVERY']);

export default function DispatchTruckBoard({ viewSwitcher }) {
    const { jobs, clients, crew, trucks, saveJob, removeJob, truckDowntime } = useStore();
    // Out of service (truck_downtime, set on the Fleet page — 2026-09-18):
    // a range covering the day greys the cell with the reason. Jobs already
    // on the truck still render (the conflict is real and needs fixing), so
    // this is a warning surface, not a filter.
    const downtimeFor = (truckId, dayIso) => (truckDowntime || []).find(r =>
        r.truck_id === truckId && r.from_date && r.from_date <= dayIso && (r.to_date || r.from_date) >= dayIso) || null;
    const { show: showToast } = useToast();
    const [weekOffset, setWeekOffset] = useState(0);
    const [editJob, setEditJob] = useState(null);

    const weekStart = useMemo(() => startOfWeek(addDays(new Date(), weekOffset * 7), { weekStartsOn: 1 }), [weekOffset]);
    const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
    const dayIsos = days.map(d => format(d, 'yyyy-MM-dd'));

    const getClient = (id) => clients.find(c => c.id === id);
    const getCrew = (id) => crew.find(c => c.id === id);
    const activeTrucks = trucks.filter(t => t.active !== false);

    // jobsByDate: dateIso → non-cancelled jobs covering that date
    // (multi-day spans occupy every day of duration_days).
    const jobsByDate = useMemo(() => {
        const map = new Map(dayIsos.map(d => [d, []]));
        for (const j of (jobs || [])) {
            if (j._isSpanDay || j.status === 'cancelled' || !j.move_date || j.type === 'WALKTHROUGH') continue;
            const dur = Math.max(1, j.duration_days || 1);
            for (let k = 0; k < dur; k++) {
                const d = format(addDays(parseISO(j.move_date), k), 'yyyy-MM-dd');
                if (map.has(d)) map.get(d).push(j);
            }
        }
        return map;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobs, dayIsos.join(',')]);

    const handleSave = async (form, deleteId) => {
        if (deleteId) { await removeJob(deleteId); showToast('Job deleted'); return; }
        await saveJob(form);
        showToast('Job updated');
    };

    // Jobs that NEED a truck but have none, per day — the amber rail.
    const needsTruck = (d) => (jobsByDate.get(d) || []).filter(j =>
        TRUCK_TYPES.has(j.type) && (j.truck_ids || []).length === 0 && j.status !== 'completed');

    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-y-2 mb-4">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 min-w-0">
                    <div className="flex items-center gap-2.5">
                        <h1 className="font-bold font-lemonmilk text-slate-900 text-2xl">Trucks</h1>
                        <HelpTip title="How the truck board works">
                            <p>The week through the fleet's eyes: each row is a truck, each column a day, each pill a job that truck is on (colored by the job's crew lead; multi-day runs occupy every day of their span). <span className="font-semibold">Red-ringed pills</span> mean the truck is on two different jobs that day — check the times before assuming it's a problem (a pack AM + move PM on one truck is normal). Grey <span className="font-semibold">HOLD</span> chips are loaded trucks parked between LOAD and DRIVE — the truck is committed, no crew is.</p>
                            <p>The amber <span className="font-semibold">"Needs truck"</span> row lists truck-type jobs with no truck assigned yet. Tap any pill to open the job editor; truck reassignment happens on the Board via Truck Assignment (the chaining rules live there).</p>
                            <p>A <span className="font-semibold">grey hatched cell</span> marked "Out — brakes" is a truck out of service that day (set on the Fleet page, Out of service). Capacity leaves it out of the truck count; any pill still sitting in that cell needs a different truck.</p>
                        </HelpTip>
                    </div>
                    {viewSwitcher}
                </div>
                <div className="flex items-center gap-1">
                    <IconButton label="Previous week" size="sm" onClick={() => setWeekOffset(w => w - 1)}>←</IconButton>
                    <Button variant={weekOffset === 0 ? 'primary' : 'secondary'} size="sm" onClick={() => setWeekOffset(0)}>This Week</Button>
                    <IconButton label="Next week" size="sm" onClick={() => setWeekOffset(w => w + 1)}>→</IconButton>
                    <span className="ml-2 font-semibold text-slate-800 text-sm whitespace-nowrap">{format(weekStart, 'MMM d')} – {format(addDays(weekStart, 6), 'MMM d, yyyy')}</span>
                </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
                <div className="grid" style={{ gridTemplateColumns: `150px repeat(7, minmax(120px, 1fr))`, minWidth: 1000 }}>
                    {/* header row */}
                    <div className="bg-slate-100 border-b border-r border-slate-300 px-3 py-2 text-xs font-bold text-slate-700">Truck</div>
                    {days.map((d, i) => (
                        <div key={i} className={`bg-slate-100 border-b border-slate-300 px-2 py-2 text-center ${isToday(d) ? 'bg-red-50' : ''}`}>
                            <div className="text-[10px] font-bold text-slate-500 uppercase">{format(d, 'EEE')}</div>
                            <div className={`text-sm font-bold ${isToday(d) ? 'text-red-600' : 'text-slate-900'}`}>{format(d, 'MMM d')}</div>
                        </div>
                    ))}

                    {/* needs-truck rail */}
                    <div className="border-b-2 border-amber-300 border-r border-slate-200 px-3 py-2 flex items-center gap-2" style={{ background: '#fffbeb' }}>
                        <div className="w-2.5 h-8 rounded-full bg-amber-400 flex-shrink-0"></div>
                        <span className="text-sm font-bold text-amber-800">Needs truck</span>
                    </div>
                    {dayIsos.map((d, i) => {
                        const list = needsTruck(d);
                        return (
                            <div key={i} className="border-b-2 border-amber-300 px-1.5 py-1.5 space-y-1" style={{ background: '#fffbeb' }}>
                                {list.map(j => {
                                    const client = getClient(j.client_id);
                                    return (
                                        <button key={j.id} onClick={() => setEditJob(j)}
                                            title={`${client?.name || 'Unknown'} · ${formatTime12(j.time) || 'no time'} · needs a truck`}
                                            className="w-full text-left text-[11px] font-semibold bg-white border border-amber-300 text-amber-900 rounded-md px-1.5 py-1 truncate hover:bg-amber-100">
                                            {client?.name || 'Unknown'}
                                        </button>
                                    );
                                })}
                            </div>
                        );
                    })}

                    {/* truck rows */}
                    {activeTrucks.map(truck => (
                        <React.Fragment key={truck.id}>
                            <div className="border-b border-r border-slate-200 px-3 py-2 flex items-center gap-2 min-w-0">
                                <span className="text-sm font-bold text-slate-800 truncate">{truck.name}</span>
                            </div>
                            {dayIsos.map((d, i) => {
                                const cellJobs = (jobsByDate.get(d) || []).filter(j => (j.truck_ids || []).includes(truck.id));
                                // Same-lead rule (2026-08-07): several jobs on
                                // one truck is only a CONFLICT when they span
                                // different crew leads — one lead running his
                                // truck job-to-job all day is the normal small-
                                // job pattern (five deliveries, one 16').
                                // Leaderless jobs count as distinct (ownership
                                // unknown → stay loud).
                                const conflict = new Set(cellJobs.map(j => j.leader_id || `job:${j.id}`)).size > 1;
                                const down = downtimeFor(truck.id, d);
                                return (
                                    <div key={i}
                                        title={down ? `${truck.name} is out of service${down.reason ? ` — ${down.reason}` : ''} (${down.from_date}${down.to_date && down.to_date !== down.from_date ? ` → ${down.to_date}` : ''}). Set on the Fleet page.` : undefined}
                                        className={`border-b border-slate-200 px-1.5 py-1.5 space-y-1 ${down ? 'bg-slate-100' : isToday(parseISO(d)) ? 'bg-red-50/40' : ''}`}
                                        style={down ? { backgroundImage: 'repeating-linear-gradient(135deg, transparent 0 6px, rgba(100,116,139,0.10) 6px 12px)' } : undefined}>
                                        {down && (
                                            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wide truncate">
                                                Out{down.reason ? ` — ${down.reason}` : ''}
                                            </div>
                                        )}
                                        {cellJobs.map(j => {
                                            const client = getClient(j.client_id);
                                            const lead = getCrew(j.leader_id);
                                            const phase = j.phase_kind && kindShortLabel(j.phase_kind);
                                            const midSpan = j.move_date !== d;
                                            const dayN = midSpan ? Math.round((parseISO(d) - parseISO(j.move_date)) / 86400000) + 1 : 1;
                                            // Ghost hold (pick C): the truck is parked loaded —
                                            // a grey chip, no lead colour, still a conflict if
                                            // anything else wants this truck today.
                                            if (kindIsHoldOnly(j.phase_kind)) {
                                                const days = Math.max(1, j.duration_days || 1);
                                                return (
                                                    <button key={j.id} onClick={() => setEditJob(j)}
                                                        title={`${client?.name || 'Unknown'} · loaded truck parked${days > 1 ? ` · day ${dayN}/${days}` : ''}${conflict ? ' · ⚠ another job wants this truck today' : ''}`}
                                                        className={`w-full text-left text-[11px] font-semibold rounded-md px-1.5 py-1 flex items-center gap-1 bg-slate-100 border border-slate-300 text-slate-600 hover:bg-slate-200 ${conflict ? 'ring-2 ring-red-500' : ''}`}>
                                                        <span className="text-[9px] font-black tracking-wider">HOLD</span>
                                                        <span className="truncate flex-1">· {client?.name || 'Unknown'}</span>
                                                        {days > 1 && <span className="text-[9px] text-slate-400">{dayN}/{days}</span>}
                                                        {conflict && <span className="flex-shrink-0">⚠</span>}
                                                    </button>
                                                );
                                            }
                                            return (
                                                <button key={j.id} onClick={() => setEditJob(j)}
                                                    title={`${client?.name || 'Unknown'} · ${midSpan ? `day ${Math.round((parseISO(d) - parseISO(j.move_date)) / 86400000) + 1}/${j.duration_days}` : (formatTime12(j.time) || 'no time')}${lead ? ` · Lead: ${lead.name}` : ''} · ${j.status}${conflict ? ' · ⚠ truck shared across crews this day' : ''}`}
                                                    className={`w-full text-left text-[11px] font-semibold text-white rounded-md px-1.5 py-1 flex items-center gap-1 ${conflict ? 'ring-2 ring-red-500' : ''} ${j.status === 'completed' ? 'opacity-50' : ''}`}
                                                    style={{ backgroundColor: lead?.display_color || '#64748b' }}>
                                                    <span className="truncate flex-1">{client?.name || 'Unknown'}</span>
                                                    {phase && <span className="text-[9px] font-extrabold bg-black/25 rounded px-1 flex-shrink-0">{phase}</span>}
                                                    {conflict && <span className="flex-shrink-0">⚠</span>}
                                                </button>
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </React.Fragment>
                    ))}
                </div>
            </div>

            {editJob && (
                <JobModal
                    job={editJob}
                    onClose={() => setEditJob(null)}
                    onSave={async (form, deleteId) => { await handleSave(form, deleteId); setEditJob(null); }}
                    clients={clients} crew={crew} trucks={trucks} jobs={jobs}
                />
            )}
        </div>
    );
}
